/**
 * "Dependencies" gadget (Build): from the Quality overview (the same answer as Tests and Security), the outdated
 * packages per service (the dependencies check; only when registry lookups are on, else the check's own note says
 * why) and the vulnerable packages the vulnerabilities check lists first, with its total. Before the checks ran:
 * says so, with a link to Verify › Quality.
 */
import React from 'react';
import PropTypes from 'prop-types';
import { dependencySummary } from '../../../utils/homeGadgets/build';
import GadgetCard, { GadgetBody, TabLink } from './GadgetCard';
import { useQualityOverview } from './sources';

export default function DependenciesGadget({ projectId, onOpenQuality = null }) {
  const { data, error, loading } = useQualityOverview(projectId);
  const summary = dependencySummary(data);
  const meta = summary.ran
    ? [summary.outdatedTotal !== null ? `${summary.outdatedTotal} outdated` : null, summary.vulnerableTotal !== null ? `${summary.vulnerableTotal} vulnerable` : null].filter(Boolean).join(' · ')
    : null;
  return (
    <GadgetCard links={<TabLink onClick={onOpenQuality}>Verify › Quality</TabLink>} meta={meta} testId="dependencies-gadget" title="Dependencies">
      <GadgetBody
        data={data}
        empty={!summary.ran}
        emptyText="The dependency checks have not run yet. Run them in Quality."
        error={error}
        errorText="The Quality checks could not be read just now."
        loading={loading}
        loadingText="Reading the dependency checks…"
      >
        {() => (
          <>
            {summary.services.length === 0 ? (
              <p className="ph-quiet">{summary.outdatedKnown ? 'Every package is up to date and none is known to be vulnerable.' : 'No known vulnerable package.'}</p>
            ) : (
              <ul className="gd-rows">
                {summary.services.map((row) => (
                  <li className="gd-row" data-testid={`deps-${row.service}`} key={row.service}>
                    <strong>{row.service}</strong>
                    <span className="gd-grow ph-muted">
                      {row.vulnerable.length > 0 && <span className="gd-bad">{row.vulnerable.map((v) => `${v.package}${v.version ? ` ${v.version}` : ''}${v.fixed ? ` → ${v.fixed}` : ''}`).join(', ')}</span>}
                      {row.vulnerable.length > 0 && row.outdated.length > 0 && ' · '}
                      {row.outdated.length > 0 && `${row.outdated.length} outdated${row.outdated.some((o) => o.major) ? ` (${row.outdated.filter((o) => o.major).length} a major version behind)` : ''}`}
                    </span>
                  </li>
                ))}
              </ul>
            )}
            {summary.outdatedNote && <p className="gd-note" data-testid="deps-outdated-note">{summary.outdatedNote.replace(/\.$/, '')}.</p>}
          </>
        )}
      </GadgetBody>
    </GadgetCard>
  );
}

DependenciesGadget.propTypes = { onOpenQuality: PropTypes.func, projectId: PropTypes.string.isRequired };
